import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointermove', this.#onPointerMove);
    this.#clearIntent();
    clearTimeout(this.#closeTimer);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /*
   * VG Nav-Hover-Fix 2026-10-05: Hover-Intent statt Horizon-Safety-Box.
   * Die Safety-Box (data-safety-box auf dem Link, bei jedem pointermove neu gesetzt) stylte das
   * ::after des Trigger-Links unter dem Cursor laufend um. vgc-nav.css nutzt genau dieses ::after als
   * Unterstrich; zusammen feuerte Chrome falsche pointerleave-Events auf das <li>, das Panel schloss
   * sofort (auch beim Weg ins Panel). Jetzt: Öffnen erst bei ruhender Maus, Wechsel zum Nachbarn nur
   * wenn die Maus nicht gerade Richtung Panel unterwegs ist, Schließen erst nach Puffer und nur, wenn
   * der echte Hit-Test die Maus außerhalb von Trigger und Panel sieht.
   */

  /** Prüfintervall, bevor ein Panel aus dem geschlossenen Zustand öffnet (ms) */
  static OPEN_DELAY = 90;
  /** Prüfintervall, bevor bei offenem Panel auf einen anderen Menüpunkt gewechselt wird (ms) */
  static SWITCH_DELAY = 130;
  /** Puffer vor dem Schließen; Wiedereintritt bricht ihn ab (ms) */
  static CLOSE_DELAY = 250;
  /** Bewegt sich die Maus im Prüfintervall weiter als das, gilt sie als unterwegs (px) */
  static REST_DISTANCE = 20;

  /** @type {{ x: number, y: number }} */
  #pointer = { x: -1, y: -1 };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #intentTimer;

  /** @type {HTMLElement | null} */
  #intentTarget = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    this.#pointer.x = event.clientX;
    this.#pointer.y = event.clientY;
  };

  /**
   * Is the pointer really over this list item or its open submenu (actual hit test, not boundary events)?
   * @param {Element | null | undefined} listItem
   */
  #pointerIsOver(listItem) {
    if (!listItem || this.#pointer.x < 0) return false;
    const target = document.elementFromPoint(this.#pointer.x, this.#pointer.y);
    return Boolean(target && listItem.contains(target));
  }

  #clearIntent() {
    clearTimeout(this.#intentTimer);
    this.#intentTimer = undefined;
    this.#intentTarget = null;
  }

  /**
   * Activate the hovered list item once the pointer has come to rest on it.
   * @param {HTMLElement} listItem
   */
  #scheduleIntent(listItem) {
    this.#clearIntent();
    this.#intentTarget = listItem;

    const isSwitch = Boolean(this.#state.activeItem);
    const start = { ...this.#pointer };

    const check = () => {
      if (this.#intentTarget !== listItem) return;
      if (!this.#pointerIsOver(listItem)) {
        this.#clearIntent();
        return;
      }

      const dx = this.#pointer.x - start.x;
      const dy = this.#pointer.y - start.y;
      const stillMoving = Math.hypot(dx, dy) > HeaderMenu.REST_DISTANCE;
      // Mit offenem Panel: Bewegung nach unten ist der Weg ins Panel, nicht der Wunsch zu wechseln
      const headingToPanel = isSwitch && dy > 2;

      if (stillMoving || headingToPanel) {
        start.x = this.#pointer.x;
        start.y = this.#pointer.y;
        this.#intentTimer = setTimeout(check, isSwitch ? HeaderMenu.SWITCH_DELAY : HeaderMenu.OPEN_DELAY);
        return;
      }

      this.#clearIntent();
      this.#activateNow(listItem);
    };

    this.#intentTimer = setTimeout(check, isSwitch ? HeaderMenu.SWITCH_DELAY : HeaderMenu.OPEN_DELAY);
  }

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      const activeItem = this.#state.activeItem;
      if (!activeItem) return;
      if (this.#pointerIsOver(activeItem.closest('.menu-list__list-item'))) return;
      // Ein anderer Menüpunkt wartet noch auf seinen Intent: der entscheidet über Wechsel oder Schließen
      if (this.#intentTarget && this.#pointerIsOver(this.#intentTarget)) return;
      this.#deactivate();
    }, HeaderMenu.CLOSE_DELAY);
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item: mouse and pen via hover intent, focus and touch immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = /** @type {HTMLElement} */ (event.target);
    const isHover = event.type === 'pointerenter' && 'pointerType' in event && event.pointerType !== 'touch';

    if (!isHover) {
      clearTimeout(this.#closeTimer);
      this.#clearIntent();
      this.#activateNow(listItem);
      return;
    }

    if (event.clientX || event.clientY) {
      this.#pointer.x = event.clientX;
      this.#pointer.y = event.clientY;
    }

    const item = findMenuItem(listItem);
    if (item && item === this.#state.activeItem) {
      // Wiedereintritt: Schließen abbrechen, einen angefangenen Wechsel verwerfen
      clearTimeout(this.#closeTimer);
      this.#clearIntent();
      return;
    }

    if (this.#intentTarget !== listItem) this.#scheduleIntent(listItem);
  };

  /**
   * Activate the menu item of a list item immediately
   * @param {HTMLElement} listItem
   */
  #activateNow(listItem) {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!this.headerComponent) return;

    let item = findMenuItem(listItem);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = listItem.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            // VG 16.10.2026: kleine Dropdowns setzen keine Unterlagen-Höhe (siehe unten)
            if (!isDefaultSlot || submenu.classList.contains('vg-dropdown')) return;
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    // VG 16.10.2026 (Heinrich): kleine Dropdowns (Wissen, Hilfe, „Mehr“) öffnen und schließen nur ihr
    // eigenes Panel. Ohne Höhe bleibt die Vollbreiten-Unterlage (.header__underlay-open) zu, sonst fährt sie
    // beim Einklappen über die ganze Breite nach oben wie beim Mega-Menü. Mega-Panels bleiben unverändert.
    const isSmallDropdown = !isDefaultSlot || Boolean(submenu?.classList.contains('vg-dropdown'));
    if (isSmallDropdown) {
      finalHeight = 0;
      this.#cleanupMutationObserver();
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    clearTimeout(this.#closeTimer);
  }

  /**
   * Deactivate the active item: mouse and pen after the close delay, focus and touch immediately
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerleave' && 'pointerType' in event && event.pointerType !== 'touch') {
      this.#scheduleClose();
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    clearTimeout(this.#closeTimer);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
